import React, { useEffect, useState } from 'react';
import { AuditLog } from '@/types';
import { Activity } from 'lucide-react';
import { api } from '@/services/api';
import { formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { Pagination } from '@shared/components/common/Pagination';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@shared/components/ui/Table';
import { toUtcBoundary, getAuditMetadataSummary } from './settingsShared';

type AuditSortBy = 'occurredAt' | 'action' | 'entityType' | 'actorEmail' | 'companyId';

const isAuditSortBy = (value: string): value is AuditSortBy =>
  value === 'occurredAt' ||
  value === 'action' ||
  value === 'entityType' ||
  value === 'actorEmail' ||
  value === 'companyId';

const DEFAULT_FILTERS = {
  q: '',
  action: '',
  entityType: '',
  actorEmail: '',
  companyId: '',
  dateFrom: '',
  dateTo: ''
};

export const AuditLogsSection: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [sortBy, setSortBy] = useState<AuditSortBy>('occurredAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [filters, sortBy, sortDir, pageSize]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    api
      .listAuditLogs({
        page,
        pageSize,
        sortBy,
        sortDir,
        q: filters.q || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        actorEmail: filters.actorEmail || undefined,
        companyId: filters.companyId || undefined,
        dateFrom: filters.dateFrom ? toUtcBoundary(filters.dateFrom, 'start') : undefined,
        dateTo: filters.dateTo ? toUtcBoundary(filters.dateTo, 'end') : undefined
      })
      .then((data) => {
        if (!isActive) return;
        setLogs(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setLogs([]);
        setTotalCount(0);
        setError(normalizeErrorMessage(err, 'Failed to load audit logs.'));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, sortBy, sortDir, filters, refreshToken]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
          <div>
            <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <Activity className="w-5 h-5 text-teal-600" /> Audit Logs
            </h4>
            <p className="text-xs text-slate-500">
              Review system actions and changes across the platform.
            </p>
          </div>
          <button
            onClick={() => setRefreshToken((prev) => prev + 1)}
            className="px-4 py-2 text-xs font-bold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <input
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            placeholder="Search (action, entity, actor, request)"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
          <input
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            placeholder="Action"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          />
          <input
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            placeholder="Entity Type"
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
          />
          <input
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            placeholder="Actor Email"
            value={filters.actorEmail}
            onChange={(e) => setFilters({ ...filters, actorEmail: e.target.value })}
          />
          <input
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            placeholder="Company ID"
            value={filters.companyId}
            onChange={(e) => setFilters({ ...filters, companyId: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>
          <select
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            value={sortBy}
            onChange={(e) => {
              if (isAuditSortBy(e.target.value)) setSortBy(e.target.value);
            }}
          >
            <option value="occurredAt">Sort: Date/Time</option>
            <option value="action">Sort: Action</option>
            <option value="entityType">Sort: Entity Type</option>
            <option value="actorEmail">Sort: Actor Email</option>
            <option value="companyId">Sort: Company ID</option>
          </select>
          <select
            className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <button
            onClick={() => setFilters({ ...DEFAULT_FILTERS })}
            className="text-xs font-bold text-slate-500 hover:text-slate-700"
          >
            Clear Filters
          </button>
          <p className="text-xs text-slate-500">
            Total Records: <span className="font-semibold">{totalCount}</span>
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="w-full text-left">
            <THead className="bg-slate-50 border-b">
              <TR>
                <TH>Date</TH>
                <TH>Action</TH>
                <TH>Entity</TH>
                <TH>Actor</TH>
                <TH>Company</TH>
                <TH>Changes</TH>
                <TH>Metadata</TH>
              </TR>
            </THead>
            <TBody className="divide-y divide-slate-100">
              {isLoading ? (
                <EmptyRow colSpan={7} className="p-8 text-center text-slate-500">
                  Loading audit logs...
                </EmptyRow>
              ) : error ? (
                <EmptyRow colSpan={7} className="p-8 text-center text-red-500">
                  {error}
                </EmptyRow>
              ) : logs.length === 0 ? (
                <EmptyRow colSpan={7} className="p-8 text-center text-slate-500">
                  No audit logs found.
                </EmptyRow>
              ) : (
                logs.map((log) => {
                  const changeKeys = Object.keys(log.changes || {});
                  return (
                    <TR key={log.id} className="hover:bg-slate-50">
                      <TD className="text-xs text-slate-600">
                        {log.occurredAt ? formatUkDateTime(log.occurredAt, '—') : '—'}
                      </TD>
                      <TD className="text-xs font-bold text-slate-800">
                        {log.action}
                        {log.requestId && (
                          <div className="text-[10px] text-slate-400 mt-1">
                            Req: {log.requestId}
                          </div>
                        )}
                      </TD>
                      <TD className="text-xs text-slate-700">
                        <div className="font-semibold">{log.entityType}</div>
                        <div className="text-[10px] text-slate-400">{log.entityId || '—'}</div>
                      </TD>
                      <TD className="text-xs text-slate-700">
                        <div className="font-semibold">{log.actorEmail || log.actorId || '—'}</div>
                        <div className="text-[10px] text-slate-400">{log.actorType || '—'}</div>
                      </TD>
                      <TD className="text-xs text-slate-600">{log.companyId || '—'}</TD>
                      <TD className="text-xs text-slate-600">
                        <div className="font-semibold">
                          {changeKeys.length === 0 ? '—' : `${changeKeys.length} fields`}
                        </div>
                        {changeKeys.length > 0 && (
                          <div className="text-[10px] text-slate-400">
                            {changeKeys.slice(0, 2).join(', ')}
                            {changeKeys.length > 2 ? ` +${changeKeys.length - 2}` : ''}
                          </div>
                        )}
                      </TD>
                      <TD className="text-xs text-slate-600">
                        {getAuditMetadataSummary(log.metadata)}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
        <div className="p-4">
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
